import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Smartphone, CheckCircle2 } from "lucide-react";
import api, { BACKEND, formatApiError } from "@/lib/api";

export default function UpiPanel({ order, product, onClaimed }) {
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const amount = (order.amount / 100).toFixed(0);
  const qrSrc = order.qr_image_url
    ? `${order.qr_image_url.startsWith("/api") ? BACKEND : ""}${order.qr_image_url}`
    : `${BACKEND}/api/upi-qr?amount=${order.amount / 100}&note=${encodeURIComponent(product.title)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(order.upi_id);
      toast.success("UPI ID copy ho gayi");
    } catch {
      toast.error("Copy nahi hua — manually select karein");
    }
  };

  const claim = async () => {
    setBusy(true);
    try {
      await api.post("/payments/upi-claim", { db_order_id: order.db_order_id, utr: utr });
      toast.success("Payment details mil gaye — verification pending hai");
      onClaimed(order.db_order_id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="upi-payment-panel">
      <div className="rounded-2xl bg-slate-50 p-5 text-center">
        <img src={qrSrc} alt="UPI QR code" className="mx-auto h-52 w-52 rounded-xl bg-white object-contain" data-testid="upi-qr-img" />
        <p className="mt-3 font-display text-2xl font-extrabold">₹{Number(amount).toLocaleString("en-IN")}</p>
        <p className="text-xs text-slate-500">GPay · PhonePe · Paytm · koi bhi UPI app se scan karein</p>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">UPI ID</p>
          <p className="truncate text-sm font-bold text-slate-800">{order.upi_id}</p>
          {order.upi_name && <p className="text-xs text-slate-500">{order.upi_name}</p>}
        </div>
        <button onClick={copy} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-bold text-amber-400" data-testid="upi-id-copy-btn">
          <Copy size={13} /> Copy
        </button>
      </div>

      <a href={order.upi_uri} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 bg-white py-3 font-display text-sm font-bold text-ink sm:hidden" data-testid="upi-app-open-link">
        <Smartphone size={15} /> UPI app me kholhein
      </a>

      <div className="mt-4 space-y-3">
        <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / Transaction ID (optional)" data-testid="upi-utr-input"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20" />
        <button onClick={claim} disabled={busy} data-testid="upi-paid-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 font-display font-bold text-ink hover:bg-amber-400 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          Maine payment kar di hai — verify karein
        </button>
        <p className="text-center text-[11px] leading-relaxed text-slate-500">
          Verify hone ke baad download button khud apne aap yahan aa jayega.
        </p>
      </div>
    </div>
  );
}
